import { useEffect, useState } from "react";
import { ShieldCheck, Lock, Users, Globe2, Eye, Download, Trash2, Bot, Activity, Share2, AlertTriangle, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { MfaSettings } from "../components/MfaSettings";

/* My Privacy & Security Center — activity log, sharing overview, AI inventory, data controls. */
const GC = { blue: "#4285F4", red: "#EA4335", yellow: "#F9AB00", green: "#34A853" };

const EVENT_LABELS = {
  doc_upload: ["Document uploaded", GC.blue], doc_view: ["Document viewed", GC.green],
  doc_download: ["Document downloaded", GC.yellow], doc_delete: ["Document deleted", GC.red],
  secret_reveal: ["Stored login revealed", GC.red], share_grant: ["Sharing enabled", GC.yellow],
  share_revoke: ["Sharing revoked", GC.green], data_export: ["Data export", GC.blue],
  data_deletion: ["Data deletion", GC.red], ai_processing: ["AI processing", GC.blue],
  mfa_change: ["2-step verification changed", GC.blue],
};

const SHIELDS = [
  { icon: Lock, label: "Private", desc: "Only you — the default for everything in My Travel Hub", color: GC.green },
  { icon: Users, label: "Family / Selected", desc: "Only people you explicitly add to a trip, with view or edit roles", color: GC.blue },
  { icon: Share2, label: "Shared link", desc: "Summary-only trip links — documents, PNRs & seats always stay private", color: GC.yellow },
  { icon: Globe2, label: "Public / Community", desc: "Only what you explicitly publish (posts, moderated visa links)", color: GC.red },
];

const DELETE_MODULES = [["bags", "My Baggage"], ["insurance", "Insurance policies"], ["loyalty", "Loyalty programs"],
  ["journeys", "Journeys, boarding passes & companion chats"], ["visas", "Visa records & links"],
  ["trips", "Trips (incl. journeys & travelers)"], ["all", "Entire Travel Hub data"]];

const Section = ({ icon: Icon, color, title, subtitle, children, testid }) => (
  <div className="bg-white border border-zinc-200 mb-6" data-testid={testid}>
    <div className="h-1" style={{ background: color }} />
    <div className="p-5">
      <h2 className="font-heading font-900 text-xl flex items-center gap-2" style={{ color }}>
        <Icon className="w-5 h-5" /> {title}
      </h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-0.5 mb-3">{subtitle}</p>}
      {children}
    </div>
  </div>
);

export default function PrivacySecurity() {
  const [activity, setActivity] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [docShares, setDocShares] = useState([]);
  const [delModule, setDelModule] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/privacy/activity").then((r) => setActivity(r.data.items)).catch(() => setActivity([]));
    api.get("/privacy/sharing").then((r) => setSharing(r.data)).catch(() => setSharing(null));
    api.get("/privacy/inventory").then((r) => setInventory(r.data)).catch(() => setInventory(null));
    api.get("/doc-shares").then((r) => setDocShares(r.data.items)).catch(() => setDocShares([]));
  }, []);

  const revokeShare = async (sid) => {
    try {
      await api.post(`/doc-shares/${sid}/revoke`);
      toast.success("Access revoked");
      setDocShares((l) => l.map((s) => (s.id === sid ? { ...s, status: "revoked", revoked: true } : s)));
    } catch { toast.error("Could not revoke"); }
  };

  const exportData = async () => {
    setBusy(true);
    try {
      const r = await api.get("/privacy/export", { timeout: 60000 });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "travelerpulse-my-data.json";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Your data export has been downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    setBusy(false);
  };

  const deleteData = async () => {
    if (!delModule) { toast.error("Choose what to delete"); return; }
    if (confirmText !== "DELETE") { toast.error('Type "DELETE" to confirm'); return; }
    setBusy(true);
    try {
      const r = await api.post("/privacy/delete", { module: delModule, confirm: "DELETE" });
      toast.success(`Deleted ${Object.values(r.data.removed).reduce((a, b) => a + b, 0)} records`);
      setDelModule(""); setConfirmText("");
      api.get("/privacy/activity").then((x) => setActivity(x.data.items)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Deletion failed"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-zinc-50 py-10">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
          <ShieldCheck className="w-10 h-10" style={{ color: GC.blue }} /> My Privacy &amp; Security
        </h1>
        <p className="text-muted-foreground mt-2 mb-8 text-sm">Private by default. Minimum data. Encrypted in transit and at rest. You control what's shared, exported and deleted.</p>

        <Section icon={Lock} color={GC.green} title="Privacy Shield" subtitle="Every piece of information in your Travel Hub carries one of these levels:" testid="privacy-shield">
          <div className="grid sm:grid-cols-2 gap-3">
            {SHIELDS.map((s) => (
              <div key={s.label} className="flex items-start gap-2.5 border border-zinc-200 p-3">
                <s.icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: s.color }} />
                <div><div className="text-sm font-700">{s.label}</div><div className="text-xs text-muted-foreground">{s.desc}</div></div>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Smartphone} color={GC.blue} title="2-Step Verification (MFA)" subtitle="Protect your account with an authenticator app — a second code is required after every sign-in." testid="privacy-mfa">
          <MfaSettings />
        </Section>

        <Section icon={Activity} color={GC.blue} title="Security Activity" subtitle="Recent security-relevant events on your account (uploads, views, downloads, reveals, sharing, exports). Contents of your documents are never logged." testid="privacy-activity">
          {!activity ? <p className="text-sm text-muted-foreground">Loading…</p>
            : activity.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="privacy-activity-empty">No security events yet.</p>
            : (
              <ul className="divide-y divide-zinc-100 max-h-96 overflow-y-auto">
                {activity.map((e) => {
                  const [label, color] = EVENT_LABELS[e.event] || [e.event, "#666"];
                  return (
                    <li key={e.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`privacy-event-${e.id}`}>
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                      <span className="font-600">{label}</span>
                      <span className="text-xs text-muted-foreground truncate">{Object.entries(e.meta || {}).filter(([k]) => !k.endsWith("_id")).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
                      <span className="ml-auto text-xs font-mono text-muted-foreground shrink-0">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
                    </li>
                  );
                })}
              </ul>
            )}
        </Section>

        <Section icon={Share2} color={GC.yellow} title="Who has access?" subtitle="Everything defaults to private. This is everything you've explicitly shared." testid="privacy-sharing">
          {!sharing ? <p className="text-sm text-muted-foreground">Loading…</p> : (
            <div className="space-y-4 text-sm">
              <div>
                <div className="font-700 mb-1">Trips I've shared</div>
                {sharing.my_shared_trips.length === 0 ? <p className="text-muted-foreground text-xs" data-testid="privacy-sharing-none">None — all your trips are private.</p> : (
                  sharing.my_shared_trips.map((t) => (
                    <div key={t.trip_id} className="border border-zinc-200 p-2.5 mb-1.5" data-testid={`privacy-shared-trip-${t.trip_id}`}>
                      <b>{t.title || "Trip"}</b> — {t.privacy}{t.public_summary_link && " · public summary link active"}
                      {t.recipients.length > 0 && <div className="text-xs text-muted-foreground mt-0.5">{t.recipients.map((r) => `${r.email} (${r.role})`).join(", ")}</div>}
                    </div>
                  ))
                )}
              </div>
              {sharing.shared_with_me.length > 0 && (
                <div><div className="font-700 mb-1">Shared with me</div>
                  {sharing.shared_with_me.map((t) => <div key={t.trip_id} className="text-xs text-muted-foreground">{t.title || "Trip"}</div>)}</div>
              )}
              {sharing.community_links.length > 0 && (
                <div><div className="font-700 mb-1">My community visa links</div>
                  {sharing.community_links.map((l) => <div key={l.id} className="text-xs text-muted-foreground">{l.country} — {l.title} ({l.share_status})</div>)}</div>
              )}
              <div>
                <div className="font-700 mb-1">Expiring document shares</div>
                {docShares.length === 0 ? <p className="text-muted-foreground text-xs" data-testid="privacy-docshares-none">No document shares — sensitive documents are only ever shared when you create a time-limited link.</p> : (
                  docShares.map((s) => (
                    <div key={s.id} className="border border-zinc-200 p-2.5 mb-1.5 flex flex-wrap items-center gap-2" data-testid={`privacy-docshare-${s.id}`}>
                      <b className="text-sm">{s.label}</b>
                      <span className={`text-[10px] font-800 uppercase px-1.5 py-0.5 rounded ${s.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"}`} data-testid={`privacy-docshare-status-${s.id}`}>{s.status}</span>
                      <span className="text-xs text-muted-foreground">expires {new Date(s.expires_at).toLocaleString()} · {s.views} view{s.views === 1 ? "" : "s"}</span>
                      {s.status === "active" && (
                        <button onClick={() => revokeShare(s.id)} data-testid={`privacy-docshare-revoke-${s.id}`}
                          className="ml-auto text-xs font-600 border border-rose-300 text-rose-700 px-2 py-1 hover:bg-rose-50">Revoke now</button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </Section>

        <Section icon={Bot} color={GC.blue} title="AI & Data Inventory" subtitle="Exactly which AI services see your data, what they receive and why. Your documents are never used for model training." testid="privacy-inventory">
          {inventory && (
            <div className="space-y-3">
              {inventory.ai_providers.map((p) => (
                <div key={p.provider} className="border border-zinc-200 p-3 text-sm">
                  <div className="font-700">{p.provider}</div>
                  <div className="text-xs text-muted-foreground mt-1"><b>Used for:</b> {p.used_for.join("; ")}</div>
                  <div className="text-xs text-muted-foreground mt-0.5"><b>Data sent:</b> {p.data_sent}</div>
                  <div className="text-xs text-muted-foreground mt-0.5"><b>Retention:</b> {p.retention}</div>
                </div>
              ))}
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {inventory.classification.map((c) => (
                  <div key={c.level} className="border border-zinc-200 p-2.5">
                    <div className="text-xs font-800 uppercase tracking-wide" style={{ color: c.shield === "highly_sensitive" ? GC.red : c.shield === "sensitive" ? GC.yellow : c.shield === "personal" ? GC.blue : GC.green }}>{c.level}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{c.examples}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section icon={Eye} color={GC.red} title="Data Controls" subtitle="Your data belongs to you — export it or delete it at any time." testid="privacy-controls">
          <button onClick={exportData} disabled={busy} data-testid="privacy-export-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-600 disabled:opacity-50" style={{ background: GC.blue }}>
            <Download className="w-4 h-4" /> Export my data (JSON)
          </button>
          <div className="mt-5 border border-rose-200 p-4">
            <div className="text-sm font-800 text-rose-700 flex items-center gap-1.5 mb-2"><AlertTriangle className="w-4 h-4" /> Delete my data</div>
            <p className="text-xs text-muted-foreground mb-2">Permanently removes the selected records from your Travel Hub. This cannot be undone.</p>
            <div className="flex flex-wrap items-center gap-2">
              <select value={delModule} onChange={(e) => setDelModule(e.target.value)} data-testid="privacy-delete-module"
                className="border border-zinc-300 px-2.5 py-2 text-sm bg-white">
                <option value="">Choose what to delete…</option>
                {DELETE_MODULES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder='Type "DELETE"'
                data-testid="privacy-delete-confirm" className="border border-zinc-300 px-2.5 py-2 text-sm w-32" />
              <button onClick={deleteData} disabled={busy} data-testid="privacy-delete-btn"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-white text-sm font-600 disabled:opacity-50" style={{ background: GC.red }}>
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </Section>

        <p className="text-xs text-muted-foreground mt-2">Security is risk management, not a guarantee. TravelerPulse applies defense-in-depth (verified uploads, row-level authorization, private storage, rate limiting, audit logging) and continuously improves. Report concerns via the feedback form.</p>
      </div>
    </div>
  );
}
